import { useMemo, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Input, Label, MarkdownContent, Textarea } from '@/components/ui';
import type { AskUserAnswer, AskUserQuestion } from '@/types/inbox';

interface AskUserQuestionFieldProps {
  question: AskUserQuestion;
  answer: AskUserAnswer | undefined;
  onChange: (next: AskUserAnswer) => void;
  disabled?: boolean;
  /**
   * When set, `confirm` questions render no inline Yes/No control — the host
   * (e.g. the inline conversation card) renders the choices in its action bar.
   */
  suppressConfirmInput?: boolean;
}

// Legacy sentinel, retained only for back-compat with `isAnswerComplete`.
// The redesigned fields no longer emit it — "Other" is a reserved text input
// whose typed value becomes the answer directly.
export const OTHER_TOKEN = '__other__';

export function isAnswerComplete(value: string | string[] | undefined): boolean {
  if (value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0 && !value.includes(OTHER_TOKEN);
  return value !== '' && value !== OTHER_TOKEN;
}

export function AskUserQuestionField({
  question,
  answer,
  onChange,
  disabled,
  suppressConfirmInput,
}: AskUserQuestionFieldProps) {
  const value = answer?.value;
  const comment = answer?.comment ?? '';

  const labelId = useMemo(() => `q-${question.questionId}`, [question.questionId]);

  function update(next: Partial<AskUserAnswer>) {
    onChange({
      questionId: question.questionId,
      value: next.value !== undefined ? next.value : value ?? '',
      ...(next.comment !== undefined ? { comment: next.comment } : { comment: comment || undefined }),
    });
  }

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend id={labelId}>
        <MarkdownContent size="sm" className="text-foreground">
          {question.question}
        </MarkdownContent>
      </legend>
      {question.header && (
        <p className="text-xs text-muted-foreground">{question.header}</p>
      )}

      {question.inputType === 'text' && (
        <Textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => update({ value: e.target.value })}
          placeholder="Type your answer…"
          aria-labelledby={labelId}
          rows={3}
        />
      )}

      {question.inputType === 'select' && (
        <SelectField
          question={question}
          value={typeof value === 'string' ? value : ''}
          onChange={(v) => update({ value: v })}
          disabled={disabled}
        />
      )}

      {question.inputType === 'multi_select' && (
        <MultiSelectField
          question={question}
          values={Array.isArray(value) ? value : []}
          onChange={(v) => update({ value: v })}
          disabled={disabled}
        />
      )}

      {question.inputType === 'confirm' && !suppressConfirmInput && (
        <div className="flex gap-2">
          <ConfirmButton
            label="Yes"
            selected={value === 'yes'}
            onClick={() => update({ value: 'yes' })}
          />
          <ConfirmButton
            label="No"
            selected={value === 'no'}
            onClick={() => update({ value: 'no' })}
          />
        </div>
      )}

      {question.inputType === 'review' && (
        // Review questions render no input — the parent ReviewCard owns the
        // approve / respond actions. Show only the prompt + surface hint.
        <p className="text-xs text-muted-foreground">
          {question.surfaceRef?.surfaceName
            ? `Surface: ${question.surfaceRef.surfaceName}`
            : 'Use the actions below to respond.'}
        </p>
      )}

      {question.allowComment && question.inputType !== 'review' && (
        <div className="space-y-1">
          <Label htmlFor={`${labelId}-comment`} className="text-xs text-muted-foreground">
            Comment (optional)
          </Label>
          <Textarea
            id={`${labelId}-comment`}
            value={comment}
            onChange={(e) => update({ comment: e.target.value })}
            rows={2}
            placeholder="Add a note…"
          />
        </div>
      )}
    </fieldset>
  );
}

// ─── Choice group (shared by single- and multi-select) ───────────────────────

const ROW_BASE =
  'focus-ring flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60';

function rowClass(selected: boolean): string {
  return (
    ROW_BASE +
    ' ' +
    (selected
      ? 'border-primary/50 bg-primary/10'
      : 'border-border bg-background hover:bg-accent/40')
  );
}

/**
 * Control-free option list: the whole row is the target (no radio/checkbox
 * glyph). Selection is shown by a highlighted row + a trailing check. Keyboard:
 * Up/Down (and Home/End) move focus between rows, Space/Enter toggle/select.
 */
function ChoiceGroup({
  question,
  multiple,
  selectedLabels,
  otherValue,
  onActivate,
  onOtherChange,
  disabled,
}: {
  question: AskUserQuestion;
  multiple: boolean;
  selectedLabels: string[];
  otherValue: string;
  onActivate: (label: string) => void;
  onOtherChange: (next: string) => void;
  disabled?: boolean;
}) {
  const options = question.options ?? [];
  const showOther = question.allowOther !== false;
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [otherFocused, setOtherFocused] = useState(false);
  // "Other" reads as selected while it holds text, or while focused (so it
  // highlights on focus and clears again on blur when nothing was typed).
  const otherSelected = !!otherValue || otherFocused;

  function focusRow(i: number) {
    const clamped = Math.max(0, Math.min(options.length - 1, i));
    setActiveIndex(clamped);
    rowRefs.current[clamped]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent, i: number) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(i + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(options.length - 1);
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        onActivate(options[i].label);
        break;
    }
  }

  return (
    <div role={multiple ? 'group' : 'radiogroup'} className="space-y-1.5">
      {options.map((opt, i) => {
        const selected = selectedLabels.includes(opt.label);
        return (
          <button
            key={opt.label}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            type="button"
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={selected}
            tabIndex={i === activeIndex ? 0 : -1}
            disabled={disabled}
            onClick={() => onActivate(opt.label)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={rowClass(selected)}
          >
            <span className="min-w-0 flex-1">
              <span className="font-medium text-foreground">{opt.label}</span>
              {opt.description && (
                <span className="ml-1.5 text-muted-foreground">{opt.description}</span>
              )}
            </span>
            {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
          </button>
        );
      })}
      {showOther && (
        <div
          className={
            'flex items-center gap-3 rounded-md border px-3 py-1.5 text-sm transition-colors ' +
            (otherSelected ? 'border-primary/50 bg-primary/10' : 'border-border bg-background')
          }
        >
          <Input
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            onFocus={() => setOtherFocused(true)}
            onBlur={() => setOtherFocused(false)}
            placeholder="Something else…"
            disabled={disabled}
            aria-label="Other answer"
            className="h-7 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
          />
          {otherValue && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </div>
      )}
    </div>
  );
}

function SelectField({
  question,
  value,
  onChange,
  disabled,
}: {
  question: AskUserQuestion;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const options = question.options ?? [];
  const isKnown = options.some((o) => o.label === value);
  const otherValue = value !== '' && !isKnown ? value : '';

  return (
    <ChoiceGroup
      question={question}
      multiple={false}
      selectedLabels={isKnown ? [value] : []}
      otherValue={otherValue}
      onActivate={(label) => onChange(label)}
      onOtherChange={(next) => onChange(next)}
      disabled={disabled}
    />
  );
}

function MultiSelectField({
  question,
  values,
  onChange,
  disabled,
}: {
  question: AskUserQuestion;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const options = question.options ?? [];
  const known = new Set(options.map((o) => o.label));
  const selectedLabels = values.filter((v) => known.has(v));
  const otherValue = values.find((v) => !known.has(v)) ?? '';

  function toggle(label: string) {
    onChange(
      values.includes(label) ? values.filter((v) => v !== label) : [...values, label],
    );
  }

  function setOther(next: string) {
    const base = values.filter((v) => known.has(v));
    onChange(next ? [...base, next] : base);
  }

  return (
    <ChoiceGroup
      question={question}
      multiple
      selectedLabels={selectedLabels}
      otherValue={otherValue}
      onActivate={toggle}
      onOtherChange={setOther}
      disabled={disabled}
    />
  );
}

function ConfirmButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'focus-ring rounded-md border px-4 py-2 text-sm font-medium transition-colors ' +
        (selected
          ? 'border-primary/50 bg-primary/10 text-foreground'
          : 'border-border bg-background text-foreground hover:bg-accent/40')
      }
    >
      {label}
    </button>
  );
}
